import React, { useMemo } from 'react';

import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { SHORT_TIME } from 'lane-shared/helpers/constants/dates';
import { doDateRangesOverlap } from 'lane-shared/helpers/dates';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { DateRangeType } from 'lane-shared/types/baseTypes/DateRangeType';
import { ReservableAvailabilityRange } from 'lane-shared/hooks/useReservableAvailabilityByRange';
import {
  findOverlappingCapacity,
  getSlotCapacityForRange,
} from 'domains-reservable-availability';
import { ChipSubLabelVariant } from '../ChipButton';
import { getChipCapacityProps } from '../ChipButton/getChipCapacityProps';

import Chips from '../Chips';

import styles from './index.scss';
import { Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import classNames from 'classnames';

const SEE_MORE_THRESHOLD = 12;

type TimeChipProps = {
  options: DateRangeType[];
  onClick: (range: DateRangeType) => void;
  value: DateRangeType;
  unavailableDateRanges?: DateRangeType[];
  timeZone?: string;
  availability?: ReservableAvailabilityRange[];
  maxQuantityPerSlot?: number;
};

export default function TimeChips({
  options,
  onClick,
  value,
  unavailableDateRanges = [],
  timeZone,
  availability = [],
  maxQuantityPerSlot,
}: TimeChipProps) {
  const { t } = useTranslation();
  const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();

  const onClickHandler = (selectedValue: DateRangeType) => {
    if (doDateRangesOverlap(selectedValue, value)) {
      onClick({ startDate: null, endDate: null });
    } else {
      onClick(selectedValue);
    }
  };

  const availableTimes = useMemo(() => {
    const pastTimeRange: DateRangeType = {
      startDate: now.startOf('day').toJSDate(),
      endDate: now.toJSDate(),
    };

    return options
      .filter(option => {
        // A slot that has already started should not be selectable,
        // regardless of booking/capacity status.
        if (maxQuantityPerSlot != null && doDateRangesOverlap(option, pastTimeRange)) return false;

        const isUnavailable = unavailableDateRanges.some(range =>
          doDateRangesOverlap(option, range)
        );

        if (!isUnavailable) return true;
        // When FF is on, re-include slots that the resolver marked as
        // Full/UserCapReached — they render as disabled chips instead of
        // disappearing from the list.

        if (maxQuantityPerSlot != null) {
          return findOverlappingCapacity(option, availability).length > 0;
        }

        return false;
      })
      .map(option => {
        let subLabel: string | undefined;
        let subLabelVariant: ChipSubLabelVariant = 'default';
        let disabled = false;

        if (maxQuantityPerSlot != null) {
          ({ subLabel, subLabelVariant, disabled } = getChipCapacityProps(
            getSlotCapacityForRange(option, availability, maxQuantityPerSlot),
            t
          ));
        }

        return {
          label: dateFormatter(option.startDate, SHORT_TIME, timeZone),
          value: option,
          subLabel,
          subLabelVariant,
          disabled,
        };
      });
  }, [options, unavailableDateRanges, availability, timeZone, maxQuantityPerSlot, t, now]);

  if (availableTimes.length === 0) {
    return (
      <div className={classNames(styles.noAvailabilityWrapper)}>
        <Icon
          name="info-circle"
          className={styles.infoIcon}
          set={ICON_SET_FONTAWESOME}
          type="far"
        />
        <p className={styles.noAvailable}>
          {t('shared.content.feature.reservable.timeSlots.noAvailability')}
        </p>
      </div>
    );
  }

  return (
    <>
      <h5 className={styles.timeChipHeader}>
        {t('web.content.feature.reservable.timeSlots.availableTimes')}
      </h5>
      <Chips<DateRangeType>
        onClick={onClickHandler}
        compare={range => doDateRangesOverlap(range, value)}
        options={availableTimes}
        itemName="time"
        hasSeeMore
        seeMoreThreshold={SEE_MORE_THRESHOLD}
        gridSize="large"
        fixedSize={maxQuantityPerSlot != null}
      />
    </>
  );
}
